import { getTranslationKey } from '../modules/utils';
import { validations } from '../modules/utils/helpers/validations';
import { ICountry } from '../modules/types';
import { getPostalCodeFormat, isValidPostalCode } from './postal-code-rules';

/** Minimal country shape the lookup needs — `ICountry[]` satisfies it. */
export type TPostalCodeCountry = Pick<ICountry, 'id' | 'code' | 'name'>;

interface IPostalCodeErrorOptions {
  /**
   * Fall back to the legacy digits-only check when the selected country has no
   * known postal code format (or the countries list is not loaded yet). Only
   * the billing address form sets this — it is the behaviour it had before.
   */
  fallbackToDigitsOnly?: boolean;
}

/**
 * Validates a postal code against the format of the selected country.
 *
 * `countryId` is the value stored in the form / on the user (`'germany'`,
 * `'netherland'`, ...); the ISO code needed to look up the format comes from
 * the countries list the API returns.
 *
 * Returns a translated error message, or `null` when the value is acceptable.
 * Empty values are always accepted here — the `required` rule reports those.
 */
export function getPostalCodeError(
  zipCode: unknown,
  countryId: string | null | undefined,
  countriesList: TPostalCodeCountry[],
  { fallbackToDigitsOnly = false }: IPostalCodeErrorOptions = {}
): string | null {
  if (typeof zipCode !== 'string' || !zipCode.trim()) {
    return null;
  }

  const country = countriesList.find(({ id }) => id === countryId);
  const format = getPostalCodeFormat(country?.code);

  if (!format) {
    return fallbackToDigitsOnly
      ? validations.onlyNumber(zipCode, { formValues: {}, titleKey: 'zipcode' }) || null
      : null;
  }

  if (isValidPostalCode(zipCode, format)) {
    return null;
  }

  if (format.digits) {
    return getTranslationKey('requiredLength', { amount: format.digits });
  }

  return getTranslationKey('zipcodeFormat', {
    country: country?.name || format.isoCode,
    example: format.example
  });
}
